import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button.jsx';
import {
  Wind, Brain, BookOpen, Clock, Video, FileText, Lock, CheckCircle2,
  PlayCircle, Download, ChevronRight, ChevronDown,
} from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import OrnateDivider from '../components/OrnateDivider';
import SEO from '../components/SEO';
import { authUrlWithNext } from '../lib/whatsapp';
import EditableText from '../components/EditableText';

/* ---------- Level 1 MasterClass curriculum ---------- */
const PHILOSOPHY_PDF_URL =
  'https://customer-assets.emergentagent.com/job_inner-ascent-1/artifacts/rzzq8amg_Siddha_Samaj_Philosophy.pdf';
const HANDOUT_PDF_URL =
  'https://customer-assets.emergentagent.com/job_ebb063e1-3e83-418f-bb38-602ef1351305/artifacts/5hpi0vin_Grahita_Shakti_Level1_v4.pdf';

const LEVEL1_CURRICULUM = [
  {
    id: 'intro-philosophy',
    group: 'Introduction',
    free: true,
    title: 'The Siddha Philosophy',
    subtitle: 'Foundational reading for the path',
    type: 'pdf',
    src: PHILOSOPHY_PDF_URL,
    duration: '15 min read',
  },
  {
    id: 'intro-video',
    group: 'Introduction',
    free: true,
    title: 'Grahita Shakti — Introduction',
    subtitle: 'What you will experience across 21 days',
    type: 'video',
    youtubeId: 'WmRQpPn3vo8',
    orientation: 'landscape',
    duration: '18 min',
  },
  {
    id: 'section-1-handout',
    group: 'Section 1',
    free: false,
    title: 'Grahita Shakti — Level 1 Handout',
    subtitle: 'Philosophy + practice reference (download for your 21-day sankalpa)',
    type: 'pdf',
    src: HANDOUT_PDF_URL,
    duration: 'PDF',
  },
  {
    id: 'section-1-surya',
    group: 'Section 1',
    free: true,
    title: 'Surya Namaskar',
    subtitle: 'Morning activation — full-body breath-led movement',
    type: 'video',
    youtubeId: '_sb7B-Kp-FA',
    orientation: 'portrait',
    duration: '12 min',
  },
  {
    id: 'section-2-kriya',
    group: 'Section 2',
    free: false,
    title: 'Jal Neti · Kapalbhati · Agya Chakra Spandan Kriya',
    subtitle: 'Cleansing kriyas and subtle-channel preparation',
    type: 'video',
    youtubeId: 'IfytomsxyxI',
    orientation: 'landscape',
    duration: '28 min',
  },
  {
    id: 'section-3-trataka',
    group: 'Section 3',
    free: false,
    title: 'Trataka',
    subtitle: 'Gaze concentration for steadying attention',
    type: 'video',
    youtubeId: 'GZNOpqzzktQ',
    orientation: 'portrait',
    duration: '10 min',
  },
  {
    id: 'section-4-nadi',
    group: 'Section 4',
    free: false,
    title: 'Nadi Shodhan · Moorcha · Shambhavi · Siddha Kriya',
    subtitle: 'Breath regulation and entry-level Siddha Kriya',
    type: 'video',
    youtubeId: 'ERlZUL3YHpE',
    orientation: 'landscape',
    duration: '45 min',
  },
];

/* ---------- YouTube embed (responsive, orientation-aware) ---------- */
const YouTubeEmbed = ({ videoId, title, orientation = 'landscape' }) => {
  // youtube-nocookie + disable annotations, keyboard, captions, related videos
  // to minimise external linking and keep students on the course page.
  const src =
    `https://www.youtube-nocookie.com/embed/${videoId}` +
    `?rel=0&modestbranding=1&iv_load_policy=3&disablekb=1` +
    `&playsinline=1&cc_load_policy=0&fs=1&color=white`;

  // Transparent click-blocker overlays neutralise the two ways YouTube lets
  // users escape to youtube.com: (1) clicking the title bar at the top,
  // (2) clicking the "Watch on YouTube" button that appears bottom-right
  // when the video is paused. We keep the central play/pause area and the
  // bottom control bar untouched so playback/fullscreen/seek still work.
  const overlays = (
    <>
      {/* Top strip: blocks video title + channel link */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 64,
          zIndex: 3,
          background: 'transparent',
          cursor: 'default',
        }}
        onClick={(e) => e.preventDefault()}
        data-testid="yt-overlay-top"
      />
      {/* Bottom-right strip: blocks "Watch on YouTube" button.
          Sits ~44px above the bottom to leave the native control bar untouched. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 44,
          right: 0,
          width: 180,
          height: 44,
          zIndex: 3,
          background: 'transparent',
          cursor: 'default',
        }}
        onClick={(e) => e.preventDefault()}
        data-testid="yt-overlay-bottom-right"
      />
    </>
  );

  const iframe = (
    <iframe
      title={title}
      src={src}
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      data-testid="youtube-embed-iframe"
    />
  );

  if (orientation === 'portrait') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          background: '#0d0600',
          borderRadius: 8,
          padding: '12px 0',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: 'min(320px, 100%)',
            paddingBottom: 'min(568px, 177.78vw)',
            height: 0,
            overflow: 'hidden',
            borderRadius: 8,
          }}
        >
          {iframe}
          {overlays}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        paddingBottom: '56.25%',
        height: 0,
        overflow: 'hidden',
        borderRadius: 8,
        background: '#0d0600',
      }}
    >
      {iframe}
      {overlays}
    </div>
  );
};

/* ---------- Level 1 MasterClass layout ---------- */
const Level1MasterClass = ({ user, isEnrolled, navigate }) => {
  const location = useLocation();
  const [activeId, setActiveId] = useState(LEVEL1_CURRICULUM[0].id);
  const [completed, setCompleted] = useState({});

  // Persist per-user progress in localStorage
  const storageKey = useMemo(
    () => `level1-progress-${user?.id || 'guest'}`,
    [user?.id]
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setCompleted(JSON.parse(raw));
    } catch { /* ignore */ }
  }, [storageKey]);

  const markComplete = (id) => {
    setCompleted((prev) => {
      const next = { ...prev, [id]: true };
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch { /* ignore */ }
      return next;
    });
    toast.success('Marked complete', { duration: 1600 });
  };

  const lessons = LEVEL1_CURRICULUM;
  const activeLesson = lessons.find((l) => l.id === activeId) || lessons[0];
  const isLocked = !activeLesson.free && !isEnrolled;

  const totalCompletable = lessons.length;
  const doneCount = lessons.filter((l) => completed[l.id]).length;
  const pct = Math.round((doneCount / totalCompletable) * 100);

  // Group lessons by .group for sidebar
  const groups = lessons.reduce((acc, l) => {
    acc[l.group] = acc[l.group] || [];
    acc[l.group].push(l);
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-8">
      {/* ===== Sidebar ===== */}
      <aside
        className="card-mystical rounded-2xl p-5 h-fit lg:sticky lg:top-28"
        data-testid="level1-sidebar"
      >
        <div className="mb-4">
          <div className="text-xs font-heading tracking-[0.35em] text-primary/60 uppercase mb-2">
            Foundation Programme
          </div>
          <h3 className="text-xl font-heading text-primary golden-text">
            Grahita Shakti
          </h3>
          <div className="mt-3 flex items-center gap-2 text-xs text-primary/70">
            <span>{doneCount}/{totalCompletable} complete</span>
            <span className="flex-1 h-1 bg-primary/10 rounded-full overflow-hidden">
              <span
                className="block h-full bg-primary/70 transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </span>
            <span className="tabular-nums">{pct}%</span>
          </div>
        </div>

        <nav className="space-y-4 max-h-[60vh] overflow-y-auto scrollbar-hide pr-1">
          {Object.entries(groups).map(([groupName, items]) => (
            <div key={groupName}>
              <div className="text-[10px] font-heading tracking-[0.35em] text-primary/50 uppercase mb-2">
                {groupName}
              </div>
              <ul className="space-y-1">
                {items.map((l) => {
                  const locked = !l.free && !isEnrolled;
                  const done = completed[l.id];
                  const isActive = activeId === l.id;
                  return (
                    <li key={l.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(l.id)}
                        data-testid={`sidebar-lesson-${l.id}`}
                        className={`w-full text-left rounded-lg px-3 py-2 text-sm transition flex items-start gap-2 ${
                          isActive
                            ? 'bg-primary/15 border border-primary/40 text-primary'
                            : 'border border-transparent text-primary/70 hover:bg-primary/5 hover:border-primary/20'
                        }`}
                      >
                        <span className="mt-0.5 shrink-0">
                          {done ? (
                            <CheckCircle2 size={14} className="text-primary" />
                          ) : locked ? (
                            <Lock size={14} className="text-primary/40" />
                          ) : l.type === 'video' ? (
                            <PlayCircle size={14} className="text-primary/70" />
                          ) : (
                            <FileText size={14} className="text-primary/70" />
                          )}
                        </span>
                        <span className="flex-1">
                          <span className="flex items-center gap-2 leading-snug">
                            <span>{l.title}</span>
                            {l.free && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-heading tracking-[0.2em] uppercase bg-primary/15 border border-primary/40 text-primary shrink-0"
                                data-testid={`lesson-free-badge-${l.id}`}
                              >
                                Free
                              </span>
                            )}
                          </span>
                          <span className="block text-[11px] text-primary/50">{l.duration}</span>
                          {locked && (
                            <span
                              className="block text-[10px] text-primary/40 italic mt-0.5"
                              data-testid={`lesson-lock-label-${l.id}`}
                            >
                              Enroll to access
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {!isEnrolled && (
          <div className="mt-5 pt-5 border-t border-primary/15">
            <Button
              onClick={() => user ? navigate('/enroll/level-1') : navigate(authUrlWithNext(location, '/enroll/level-1'))}
              className="ornate-button w-full"
              data-testid="sidebar-enroll-cta"
            >
              Enroll · ₹3,000
            </Button>
            <p className="mt-2 text-[11px] text-primary/50 text-center">
              International payments accepted
            </p>
          </div>
        )}
      </aside>

      {/* ===== Content area ===== */}
      <section data-testid="level1-content">
        {/* Hero of current lesson */}
        <div className="card-mystical rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-heading tracking-[0.35em] text-primary/60 uppercase">
              {activeLesson.group}
              {activeLesson.free ? ' · Free' : ' · Registered'}
            </div>            {!completed[activeLesson.id] && !isLocked && (
              <button
                type="button"
                onClick={() => markComplete(activeLesson.id)}
                className="text-xs text-primary/70 hover:text-primary flex items-center gap-1"
                data-testid="mark-complete-inline"
              >
                <CheckCircle2 size={14} /> Mark complete
              </button>
            )}
            {completed[activeLesson.id] && (
              <span className="text-xs text-primary flex items-center gap-1">
                <CheckCircle2 size={14} /> Completed
              </span>
            )}
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading golden-text text-glow mb-2 tracking-wide">
            {activeLesson.title}
          </h2>
          <p className="text-primary/70">{activeLesson.subtitle}</p>
        </div>

        {/* Locked overlay */}
        {isLocked ? (
          <div
            className="card-mystical rounded-2xl p-10 text-center"
            data-testid="locked-lesson-panel"
          >
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="text-primary" size={22} />
            </div>
            <h3 className="text-xl font-heading text-primary mb-2 uppercase tracking-wider">
              Registered practitioners only
            </h3>
            <p className="text-primary/70 max-w-md mx-auto mb-6">
              Enroll in the foundation programme to unlock all guided sessions, kriyas,
              handouts and the live practice cohort.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => user ? navigate('/enroll/level-1') : navigate(authUrlWithNext(location, '/enroll/level-1'))}
                className="ornate-button px-8"
                data-testid="locked-enroll-cta"
              >
                Enroll · ₹3,000 <ChevronRight size={16} />
              </Button>
              {!user && (
                <Button
                  onClick={() => navigate(authUrlWithNext(location))}
                  variant="outline"
                  className="ornate-button px-8"
                  data-testid="locked-signin-cta"
                >
                  Sign in
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="card-mystical rounded-2xl p-4 sm:p-6">
            {activeLesson.type === 'video' ? (
              <YouTubeEmbed
                key={activeLesson.id}
                videoId={activeLesson.youtubeId}
                title={activeLesson.title}
                orientation={activeLesson.orientation}
              />
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm text-primary/70">
                    <FileText size={16} />
                    <span>PDF document · {activeLesson.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={activeLesson.src}
                      target="_blank"
                      rel="noreferrer"
                      className="ornate-button inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg"
                      data-testid="open-pdf-newtab"
                    >
                      <PlayCircle size={14} /> Open in new tab
                    </a>
                    <a
                      href={activeLesson.src}
                      download
                      className="ornate-button inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg"
                      data-testid="download-pdf-link"
                    >
                      <Download size={14} /> Download
                    </a>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-primary/25 bg-black/40">
                  <iframe
                    key={activeLesson.id}
                    title={activeLesson.title}
                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                      activeLesson.src
                    )}&embedded=true`}
                    className="w-full h-[70vh]"
                    data-testid="lesson-pdf-viewer"
                  />
                </div>
                <p className="text-xs text-primary/50 text-center">
                  If the preview does not load, use the <strong>Open in new tab</strong> or{' '}
                  <strong>Download</strong> buttons above.
                </p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

/* ---------- Generic level view for 2-4 ---------- */
const GenericLevelView = ({ level, user, isCompleted, onComplete, navigate }) => (
  <>
    <div className="mb-8">
      <div className="text-sm font-heading tracking-[0.35em] text-primary/60 uppercase mb-2">
        {level.subtitle}
      </div>
      <h1 className="text-4xl md:text-6xl font-heading font-semibold mb-4 text-primary golden-text">
        {level.title}
      </h1>
      <p className="text-xl text-primary/80 mb-6">{level.description?.split('.')[0]}</p>
      <div className="flex items-center gap-4 text-sm text-primary/70">
        <div className="flex items-center gap-2">
          <Clock size={16} />
          <span>{level.estimated_duration}</span>
        </div>
        {isCompleted && (
          <div className="flex items-center gap-2 text-primary">
            <CheckCircle2 size={14} /> <span>Completed</span>
          </div>
        )}
      </div>
    </div>

    <div className="card-mystical rounded-2xl p-8 mb-8">
      <p className="text-lg leading-relaxed text-primary/80">{level.description}</p>
    </div>

    {!level.is_locked && (
      <>
        <div className="card-mystical rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Wind className="text-primary" size={20} />
            </div>
            <h2 className="text-2xl font-heading font-normal text-primary">Pranayama Practices</h2>
          </div>
          <div className="space-y-4">
            {level.pranayama_practices.map((practice) => (
              <div
                key={`pranayama-${practice.name}`}
                className="p-4 rounded-xl border border-border/40 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-primary">{practice.name}</h3>
                  <span className="text-sm text-primary/70">{practice.duration}</span>
                </div>
                <p className="text-sm text-primary/70">{practice.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="card-mystical rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Brain className="text-accent" size={20} />
            </div>
            <h2 className="text-2xl font-heading font-normal text-primary">Meditation Practices</h2>
          </div>
          <div className="space-y-4">
            {level.meditation_practices.map((practice) => (
              <div
                key={`meditation-${practice.name}`}
                className="p-4 rounded-xl border border-border/40 hover:border-accent/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-primary">{practice.name}</h3>
                  <span className="text-sm text-primary/70">{practice.duration}</span>
                </div>
                <p className="text-sm text-primary/70">{practice.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="card-mystical rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
              <BookOpen className="text-secondary" size={20} />
            </div>
            <h2 className="text-2xl font-heading font-normal text-primary">Reading Materials</h2>
          </div>
          <div className="space-y-3">
            {level.reading_materials.map((material) => (
              <div
                key={`reading-${material.title}`}
                className="flex items-center justify-between p-4 rounded-xl border border-border/40"
              >
                <span className="font-medium text-primary">{material.title}</span>
                <span className="text-sm text-primary/70">{material.duration}</span>
              </div>
            ))}
          </div>
        </div>

        {user && !isCompleted && (
          <div className="flex justify-center">
            <Button
              onClick={onComplete}
              size="lg"
              className="ornate-button px-12 py-6 text-lg"
              data-testid="complete-level-button"
            >
              Mark Level as Complete
            </Button>
          </div>
        )}

        {!user && (
          <div className="text-center">
            <p className="text-primary/70 mb-4">Sign in to track your progress</p>
            <Button
              onClick={() => navigate(authUrlWithNext(location))}
              className="ornate-button px-8"
              data-testid="signin-to-track-button"
            >
              Sign In
            </Button>
          </div>
        )}
      </>
    )}
  </>
);

/* =====================================================================
   Level-1 page sub-components: Teacher / FAQ / Enroll / Closing
   All text inside is admin-editable via <EditableText id="..." />.
   ===================================================================== */

const SANJAY_TEACHER_IMG =
  'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/ico6mczy_New%20Hero%20Image.png';

const Level1Teacher = () => (
  <section
    className="card-mystical rounded-2xl p-5 sm:p-6 mb-10 max-w-3xl mx-auto"
    data-testid="level1-teacher-section"
  >
    <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
      <div className="shrink-0">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-primary/40 bg-primary/10">
          <img
            src={SANJAY_TEACHER_IMG}
            alt="Siddha Yogi Bhairav Sanjay Natha"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            loading="lazy"
          />
        </div>
      </div>
      <div className="flex-1">
        <div className="text-[11px] font-heading tracking-[0.3em] text-primary/60 uppercase mb-1">
          <EditableText id="level1.teacher.eyebrow" defaultValue="Your Teacher" />
        </div>
        <h3 className="text-lg sm:text-xl font-heading golden-text text-glow tracking-wide mb-1">
          <EditableText id="level1.teacher.name" defaultValue="Siddha Yogi Bhairav Sanjay Natha" />
        </h3>
        <p className="text-[13px] sm:text-sm text-primary/75 italic leading-relaxed">
          <EditableText
            id="level1.teacher.credentials"
            defaultValue="12 years of Himalayan Siddha practice · Teaches from direct transmission"
          />
        </p>
      </div>
    </div>
  </section>
);

const FAQ_ITEMS = [
  { num: 1,  group: 'The practice',
    qDefault: 'I am new to meditation. Can I really begin here?',
    aDefault: 'Yes, absolutely. The course is designed precisely for someone starting fresh. Each practice is introduced in clear, manageable steps. Level 1 begins with preparation of the body, then regulation of the breath, then activation of the Kriyas, because a beginner cannot enter deep practice safely without this foundation. If you have never meditated before, you are in the right place to begin properly.' },
  { num: 2,  group: 'The practice',
    qDefault: 'What are Siddha Kriyas, and why are they needed for meditation?',
    aDefault: 'Siddha Kriyas are specific techniques transmitted within the Himalayan Siddha lineage that prepare and refine the human instrument so that deeper meditation can take hold. Most people who attempt to meditate directly find their mind too restless and their body too unsettled for awareness to remain. The Kriyas address this systematically. They work with breath, energy, and attention to create a system that can actually receive and hold the states that meditation reveals. Without this preparation, sitting often becomes either struggle or distraction.' },
  { num: 3,  group: 'The practice',
    qDefault: 'Do I need any prior yoga or pranayama experience?',
    aDefault: 'No prior experience is required. The course teaches each practice from the ground up. If you do have prior yoga or pranayama experience, the techniques here are presented in their classical form within the Siddha lineage, which may differ in subtle but important ways from what you have learned elsewhere. Approach the material with fresh attention.' },
  { num: 4,  group: 'Commitment',
    qDefault: 'Is the 21-day Sankalpa mandatory? What if I miss a day?',
    aDefault: 'The 21-day Sankalpa is offered because consistency is what produces change, not because completion is a test. The structure exists to help the practice take root. If you miss a day, simply continue from where you are. Missing one or two days here and there is normal life. What matters is the rhythm, not perfection. The point is returning to the practice with patience and warmth toward yourself, not starting over with guilt.' },
  { num: 5,  group: 'Commitment',
    qDefault: 'How much time will I need each day?',
    aDefault: 'One hour each day, ideally in the morning. This includes about 25 minutes of preparation, 10 minutes of breath kriya, and 25 minutes of the Siddha Kriyas. For many practitioners this becomes the most steadying hour of the day. Once the practice becomes familiar, you may find yourself extending the Siddha Kriya portion naturally as the depth grows.' },
  { num: 6,  group: 'Commitment',
    qDefault: 'Are the sessions live or pre-recorded? Can I follow at my own pace?',
    aDefault: 'The sessions are pre-recorded, so you can practise at the time of day that suits your life best. The course remains available whenever you wish to return to it. While one hour daily is the recommended rhythm, you have the flexibility to schedule the practice around your own routine. The videos are in English, with clear and paced instruction.' },
  { num: 7,  group: 'Health & lifestyle',
    qDefault: 'I have a medical condition. Is this practice safe for me?',
    aDefault: 'The course handout includes a clear section on cautions and contraindications. Those with high blood pressure, heart conditions, pregnancy, epilepsy, glaucoma, severe asthma, or recent surgery should consult their physician and either modify or skip specific practices. The Kriyas are powerful and require awareness of one’s own body. When in doubt, write to us before beginning, or simply skip the practices that are contraindicated for your specific situation and continue with the rest.' },
  { num: 8,  group: 'Health & lifestyle',
    qDefault: 'Do I need to follow a specific diet?',
    aDefault: 'A Sattvic diet (vegetarian, freshly prepared, simple) supports the practice significantly. Heavy meals, alcohol, excessive caffeine, and processed foods interfere directly with the subtle states the Kriyas open. You do not need to overhaul your diet on day one. You will likely notice that the practice itself begins to refine your relationship with food naturally over the 21 days. A complete guide on diet and lifestyle for practitioners is available in the blog section.' },
  { num: 9,  group: 'Health & lifestyle',
    qDefault: 'Can I do this alongside my existing spiritual practice?',
    aDefault: 'Yes, with one consideration. The 21-day Sankalpa works best when given full attention for the duration. If you are deeply established in another tradition’s practice, you may want to set it aside temporarily for these 21 days so the Siddha Kriyas can take their full effect, then integrate as you wish afterward. The techniques themselves do not conflict with any tradition. They prepare the system, and what you do with that system afterward remains your own choice.' },
  { num: 10, group: 'Health & lifestyle',
    qDefault: 'Do I need to be religious or spiritually inclined to do this practice?',
    aDefault: 'The Siddha tradition is not a religion. It is a precise inner science arrived at through direct exploration of consciousness. The practices work the same way regardless of your background, belief system, or absence of one. What is required is willingness to engage the practice honestly.' },
  { num: 11, group: 'Health & lifestyle',
    qDefault: 'Do I need any special equipment or space?',
    aDefault: 'Minimal requirements. A clean, quiet space where you will not be disturbed for one hour, a yoga mat or thick blanket, comfortable clothing that allows free movement, and a candle for the Trataka practice. That is all.' },
  { num: 12, group: 'Support & access',
    qDefault: 'What support is included with the course?',
    aDefault: 'Enrolled students receive access to the WhatsApp Sangha, a community of fellow practitioners where questions can be raised and guidance is offered. The course also includes the complete practice handout, all video sessions, the philosophical foundation document, and lifetime access to the material so you can return to it whenever you wish.' },
  { num: 13, group: 'Support & access',
    qDefault: 'Will I receive personal guidance from a teacher?',
    aDefault: 'Yes. Beyond the WhatsApp Sangha, enrolled students can book one-on-one sessions with senior practitioners from the Siddha tradition. These sessions are for fine-tuning the techniques, clarifying anything that has felt difficult, and ensuring that your practice is being approached correctly. Having a senior practitioner observe and guide you, even occasionally, makes a significant difference in how the practice deepens. This level of direct access is rare in online spiritual education.' },
  { num: 14, group: 'Support & access',
    qDefault: 'What happens after I complete Level 1?',
    aDefault: 'Level 1 builds the capacity to receive and stabilise awareness. This is the foundation upon which all deeper practice rests. Once Level 1 is well established, which for some takes one 21-day cycle and for others several rounds, Level 2 opens. Each subsequent level deepens the practice progressively. There is no rush. Many practitioners return to Level 1 repeatedly even after moving forward, as the depth available within these foundational practices continues to reveal itself over years.' },
];
const FAQ_CATEGORIES = ['All 14', 'The practice', 'Commitment', 'Health & lifestyle', 'Support & access'];

const Level1FAQ = () => {
  const [expanded, setExpanded] = useState(false);
  const [activeCat, setActiveCat] = useState('All 14');
  const [openIdx, setOpenIdx] = useState(null);
  const filtered = activeCat === 'All 14' ? FAQ_ITEMS : FAQ_ITEMS.filter((x) => x.group === activeCat);

  return (
    <section className="max-w-3xl mx-auto mb-10 px-4 sm:px-0" data-testid="level1-faq-section">
      {/* Collapsible top bar */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="faq-collapsible-bar"
        className="w-full card-mystical rounded-xl border border-primary/20 px-5 py-4 flex items-center justify-between gap-4 hover:bg-primary/[0.04] transition text-left"
      >
        <span className="text-[13px] sm:text-sm font-heading tracking-wide text-primary/85">
          <EditableText id="level1.faq.barTitle" defaultValue="Have questions before you begin?" />
        </span>
        <ChevronDown
          size={18}
          className={`text-primary shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="mt-4" data-testid="faq-expanded-panel">
          {/* Category pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-4" data-testid="level1-faq-filters">
            {FAQ_CATEGORIES.map((c) => {
              const active = c === activeCat;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => { setActiveCat(c); setOpenIdx(null); }}
                  data-testid={`faq-filter-${c}`}
                  className={`px-3 py-1 text-[11px] sm:text-[12px] rounded-full border transition tracking-wide ${
                    active
                      ? 'bg-primary/20 border-primary/55 text-primary'
                      : 'bg-primary/[0.04] border-primary/20 text-primary/70 hover:border-primary/40 hover:text-primary'
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          {/* Accordion */}
          <div className="space-y-2.5">
            {filtered.map((item) => {
              const isOpen = openIdx === item.num;
              return (
                <div
                  key={item.num}
                  className="card-mystical rounded-xl overflow-hidden border border-primary/15"
                  data-testid={`faq-item-${item.num}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIdx(isOpen ? null : item.num)}
                    className="w-full flex items-start justify-between gap-4 p-3.5 sm:p-4 text-left hover:bg-primary/[0.04] transition"
                    aria-expanded={isOpen}
                    data-testid={`faq-toggle-${item.num}`}
                  >
                    <span className="flex-1 text-[13px] sm:text-sm text-primary/90 font-heading tracking-wide">
                      <EditableText id={`level1.faq.q${item.num}`} defaultValue={item.qDefault} multiline />
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-primary shrink-0 mt-1 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-3.5 sm:px-4 pb-4 pt-1 text-[13px] sm:text-sm text-primary/80 leading-relaxed border-t border-primary/10">
                      <EditableText id={`level1.faq.a${item.num}`} defaultValue={item.aDefault} multiline paragraphs />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

const Level1EnrollBlock = ({ navigate, placement }) => (
  <section
    className="card-mystical rounded-2xl p-6 sm:p-8 mb-10 max-w-2xl mx-auto"
    data-testid={`level1-enroll-block-${placement}`}
  >
    <div className="text-center">
      <div className="text-[11px] font-heading tracking-[0.3em] text-primary/60 uppercase mb-3">
        <EditableText id="level1.enroll.eyebrow" defaultValue="Begin the practice" />
      </div>
      <h3 className="text-xl sm:text-2xl font-heading golden-text text-glow tracking-wide mb-4">
        <EditableText id="level1.enroll.heading" defaultValue="Grahita Śakti — Foundation Programme" />
      </h3>
      <div className="text-3xl sm:text-4xl font-heading font-bold golden-text text-glow mb-2">
        <EditableText id="level1.enroll.price" defaultValue="₹3,000" />
      </div>
      <p className="text-[12px] sm:text-[13px] text-primary/60 italic mb-2">
        <EditableText
          id="level1.enroll.priceLine"
          defaultValue="Self-paced · Lifetime access"
        />
      </p>
      <p className="text-[11px] font-heading tracking-[0.3em] text-primary/55 uppercase mb-6">
        <EditableText id="level1.enroll.note" defaultValue="International payments accepted" />
      </p>

      <Button
        onClick={() => navigate('/enroll/level-1')}
        size="lg"
        className="ornate-button px-8 py-5 text-sm sm:text-base"
        data-testid={`level1-enroll-cta-${placement}`}
      >
        <EditableText id="level1.enroll.cta" defaultValue="Begin the Programme" />
        <ChevronRight size={16} />
      </Button>
    </div>
  </section>
);

const Level1Closing = () => (
  <section
    className="py-12 sm:py-16 px-4 text-center max-w-3xl mx-auto"
    data-testid="level1-closing-section"
  >
    <p
      className="italic text-lg sm:text-2xl leading-relaxed"
      style={{ color: '#c8962a', fontFamily: "'IM Fell English', 'Cinzel', serif" }}
    >
      <EditableText
        id="level1.closing.line"
        defaultValue="The practice is here. The door is open. The step is yours."
      />
    </p>
  </section>
);

/* ---------- Page ---------- */
const LevelDetail = () => {
  const { levelNumber } = useParams();
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewAsVisitor, setPreviewAsVisitor] = useState(false);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchLevel = useCallback(async () => {
    try {
      const data = await api.getLevel(parseInt(levelNumber));
      setLevel(data);
    } catch (error) {
      console.error('Failed to fetch level:', error);
      toast.error('Failed to load level');
    } finally {
      setLoading(false);
    }
  }, [levelNumber]);

  useEffect(() => {
    fetchLevel();
  }, [fetchLevel]);

  const handleCompleteLevel = async () => {
    if (!user) {
      toast.error('Please sign in to complete levels');
      navigate(authUrlWithNext(location));
      return;
    }
    try {
      await api.completeLevel(parseInt(levelNumber));
      toast.success('Level completed! Well done!');
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete level');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center mystical-bg">
        <div className="text-primary/70">Loading...</div>
      </div>
    );
  }

  if (!level) {
    return (
      <div className="min-h-screen flex items-center justify-center mystical-bg">
        <div className="text-primary/70">Level not found</div>
      </div>
    );
  }

  const isCompleted = user?.completed_levels?.includes(level.level_number);
  const trulyEnrolled = !!user && (user.is_admin || user.enrolled_levels?.includes(level.level_number));
  // Admin-only "Preview as visitor" override — flips the enrolled-gating off
  // so admin can verify what a brand-new visitor sees (enroll blocks, lock labels, etc.)
  const isEnrolled = trulyEnrolled && !(user?.is_admin && previewAsVisitor);
  const isLevel1 = level.level_number === 1;

  return (
    <div className="min-h-screen mystical-bg pt-28 pb-20 relative">
      {level?.level_number === 1 && (
        <SEO
          title="Grahita Shakti — Foundation Programme · MyShaktiverse"
          description="A structured self-guided introduction to Siddha Kriya practice, rooted in the Himalayan Siddha tradition. Build the capacity to receive."
          path="/courses/1"
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {isLevel1 ? (
            <>
              {/* Page header */}
              <div className="text-center mb-8">
                <OrnateDivider variant="lotus" className="mb-5 max-w-md mx-auto" />
                <div className="text-[11px] font-heading tracking-[0.3em] text-primary/60 uppercase mb-3">
                  <EditableText id="grahita.label" defaultValue="Foundation Programme · Self-Paced" />
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold golden-text text-glow tracking-wider mb-3">
                  <EditableText id="level1.header.title" defaultValue="Grahita Śakti" />
                </h1>
                <p
                  className="text-sm sm:text-base max-w-2xl mx-auto italic"
                  style={{ color: '#c8962a', fontFamily: "'IM Fell English', 'Cinzel', serif" }}
                  data-testid="level1-hero-subtitle"
                >
                  <EditableText
                    id="level1.header.subtitle"
                    defaultValue="The first step is learning to receive."
                    multiline
                  />
                </p>

                <OrnateDivider variant="diamond" className="max-w-xs mx-auto mt-5 mb-2" />
              </div>

              {/* ===== Introduction Video ===== */}
              <section
                className="max-w-4xl mx-auto mb-10 px-4 sm:px-0"
                data-testid="level1-intro-video-section"
              >
                <div className="text-center mb-4">
                  <div className="text-[11px] font-heading tracking-[0.3em] text-primary/60 uppercase mb-2">
                    <EditableText id="level1.introVideo.eyebrow" defaultValue="Begin Here" />
                  </div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold golden-text text-glow tracking-wide">
                    <EditableText
                      id="level1.introVideo.title"
                      defaultValue="Watch the Introduction to Grahita Śakti"
                    />
                  </h2>
                </div>

                <div className="card-mystical rounded-2xl p-3 sm:p-4">
                  <YouTubeEmbed
                    videoId="WmRQpPn3vo8"
                    title="Grahita Shakti — Introduction"
                    orientation="landscape"
                  />
                </div>

                <p className="text-center text-primary/70 mt-4 text-[13px] sm:text-sm italic max-w-2xl mx-auto">
                  <EditableText
                    id="level1.introVideo.caption"
                    defaultValue="A short orientation to the path, the lineage, and what awaits in the programme."
                    multiline
                  />
                </p>
              </section>

              {/* ===== About This Programme ===== */}
              <section
                className="card-mystical rounded-2xl p-6 sm:p-8 mb-10 max-w-3xl mx-auto"
                data-testid="level1-course-description"
              >
                <div className="text-center mb-5">
                  <div className="text-[11px] font-heading tracking-[0.3em] text-primary/60 uppercase mb-2">
                    <EditableText id="level1.foundation.eyebrow" defaultValue="About This Programme" />
                  </div>
                </div>

                <p className="text-primary/85 leading-relaxed text-sm sm:text-[15px] mb-6">
                  <EditableText
                    id="level1.foundation.body"
                    defaultValue="Grahita Śakti is a structured online programme to learn foundational Siddha Kriyas. Through the practice of Pranayama, Trataka, Mudras, and Dhyana Prayog, the body, breath, and awareness are systematically prepared for deeper states of yogic awareness. The progression is gentle, clear, and rooted in the living Himalayan Siddha tradition. Personal guidance from Sanjay Ji is available throughout — until the Kriyas are truly yours."
                    multiline
                  />
                </p>

                <ul className="space-y-2.5 pt-4 border-t border-primary/15">
                  {[
                    { id: 1, text: 'Video-guided Siddha Kriya and Pranayama sessions' },
                    { id: 2, text: 'Complete practice handout and Siddha philosophy guide' },
                    { id: 3, text: 'One personal session with Sanjay Ji to fine-tune your practice' },
                  ].map(({ id: bId, text }) => (
                    <li key={`prog-${bId}`} className="flex items-start gap-3">
                      <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-[15px] text-primary/85 leading-relaxed">
                        <EditableText
                          id={`level1.programme.bullet.${bId}`}
                          defaultValue={text}
                          multiline
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* ===== Who This Is For ===== */}
              <section
                className="card-mystical rounded-2xl p-6 sm:p-8 mb-10 max-w-3xl mx-auto"
                data-testid="level1-who-for"
              >
                <div className="text-center mb-5">
                  <div className="text-[11px] font-heading tracking-[0.3em] text-primary/60 uppercase">
                    <EditableText id="level1.whoFor.eyebrow" defaultValue="Who This Is For" />
                  </div>
                </div>

                <ul className="space-y-2.5">
                  {[
                    { id: 1, text: 'You are curious about meditation but do not know where to begin' },
                    { id: 2, text: 'You have tried meditation and felt something was missing' },
                    { id: 3, text: 'You feel drawn to a genuine, lineage-based practice' },
                    { id: 4, text: 'You want a structured path, not scattered techniques' },
                  ].map(({ id: bId, text }) => (
                    <li key={`who-${bId}`} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-2" />
                      <span className="text-sm sm:text-[15px] text-primary/85 leading-relaxed">
                        <EditableText
                          id={`level1.whoFor.bullet.${bId}`}
                          defaultValue={text}
                          multiline
                        />
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="mt-5 pt-4 border-t border-primary/15 text-center text-[13px] sm:text-sm text-primary/75 italic">
                  <EditableText
                    id="level1.whoFor.tagline"
                    defaultValue="This programme moves at your own pace. No prior experience needed."
                  />
                </p>
              </section>

              {/* ===== Enroll Block (single, positioned after the bullets and Who This Is For) ===== */}
              {!isEnrolled && <Level1EnrollBlock navigate={navigate} placement="a" />}

              {/* ===== Teacher Introduction ===== */}
              <Level1Teacher />

              {/* ===== Lesson list ===== */}
              <Level1MasterClass
                user={user}
                isEnrolled={isEnrolled}
                navigate={navigate}
              />

              {/* ===== FAQ (collapsed by default) ===== */}
              <Level1FAQ />

              {/* ===== Closing line ===== */}
              <Level1Closing />
            </>
          ) : (
            <GenericLevelView
              level={level}
              user={user}
              isCompleted={isCompleted}
              onComplete={handleCompleteLevel}
              navigate={navigate}
            />
          )}
        </motion.div>
      </div>

      {/* Admin-only floating toggle — flip between "as admin / enrolled" and "as new visitor" view */}
      {user?.is_admin && isLevel1 && (
        <div
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-3 py-2 rounded-full border border-primary/40 bg-background/85 backdrop-blur shadow-lg"
          data-testid="admin-preview-toggle"
        >
          <span className="text-[10px] font-heading tracking-[0.3em] uppercase text-primary/65">
            {previewAsVisitor ? 'Visitor view' : 'Admin view'}
          </span>
          <button
            type="button"
            onClick={() => setPreviewAsVisitor((v) => !v)}
            className={`relative inline-flex w-10 h-5 items-center rounded-full transition ${
              previewAsVisitor ? 'bg-primary/60' : 'bg-primary/20'
            }`}
            aria-label="Toggle visitor preview"
            data-testid="admin-preview-toggle-button"
          >
            <span
              className={`inline-block w-4 h-4 rounded-full bg-primary transition-transform ${
                previewAsVisitor ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default LevelDetail;
